import type React from "react";
import { useAuth } from "@clerk/react";
import { Redirect, useLocation } from "wouter";

import { t } from "@workspace/strings/web";
import { Spinner } from "@/components/ui/spinner";
import { readPersistedBranding } from "@/lib/workspace-branding-storage";
import { useWorkspace } from "@/hooks/use-workspace";
import {
  canAccessRoute,
  defaultLandingPath,
  isOperatorOnly,
} from "@/lib/capabilities";
import { sanitizeRedirectPath } from "@/lib/redirect-sanitizer";

/** First two initials of a brand name, for the loading tile. */
function brandInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  const letters = words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[1][0];
  return letters.toUpperCase();
}

export function AuthLoadingScreen() {
  // Branded hand-off from the pre-JS boot splash (index.html): same tile +
  // wordmark, but theme-aware — bg-background/foreground follow the app theme
  // while the tile keeps the fixed dark device look from the brand render.
  //
  // Read at RENDER time (not module scope) so a mid-session sign-out is
  // reflected immediately. This component is also the Suspense fallback for
  // every lazy route in the authenticated tree, so hardcoding the OmniMedia
  // mark showed a branded tenant our logo on every chunk load. It renders in
  // places where no WorkspaceProvider exists, hence the direct storage read.
  const brand = readPersistedBranding();
  const initials = brand?.name ? brandInitials(brand.name) : "";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-5 bg-background">
      <div
        aria-hidden="true"
        className="flex h-[88px] w-[88px] items-center justify-center rounded-[20px] border border-[#2e2e3a] bg-[#17171c] text-4xl font-bold tracking-tight text-[#fafafa]"
      >
        {initials ? (
          // --primary is already set by applyPersistedBrandingAtBoot (main.tsx).
          <span style={{ color: "hsl(var(--primary))" }}>{initials}</span>
        ) : (
          <>
            O<span className="text-[#3713EC]">M</span>
          </>
        )}
      </div>
      <div className="text-[17px] font-bold tracking-[0.18em] text-foreground">
        {brand?.name || t("common.appName")}
      </div>
      <Spinner className="size-6 text-muted-foreground" />
    </div>
  );
}

/** Routes that do not require an active workspace to be selected. */
const WORKSPACE_OPTIONAL_PATHS = new Set([
  "/home",
  "/workspaces",
  "/account",
  "/marketplace",
  "/player-selection",
  "/control-selection",
]);

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const [location] = useLocation();

  if (!isLoaded) {
    return <AuthLoadingScreen />;
  }

  if (!isSignedIn) {
    // Preserve the destination through sign-in on web and desktop alike, so deep
    // links / shared URLs survive auth. The SignIn page reads redirect_url and
    // lands the user back on the target afterward. Sanitized so an
    // origin-escaping location (e.g. "//evil.com") never round-trips into it.
    const redirectPath = sanitizeRedirectPath(location, "/workspaces");
    return (
      <Redirect
        to={`/sign-in?redirect_url=${encodeURIComponent(redirectPath)}`}
      />
    );
  }

  return <>{children}</>;
}

function WorkspaceGuard({
  component: Component,
  path,
}: {
  component: React.ComponentType;
  path: string;
}) {
  const { activeWorkspaceId, activeWorkspace, workspaces } = useWorkspace();

  if (!activeWorkspaceId && !WORKSPACE_OPTIONAL_PATHS.has(path)) {
    if (isOperatorOnly(workspaces)) {
      return <Redirect to="/control-selection" />;
    }
    return <Redirect to="/workspaces" />;
  }

  if (activeWorkspace && !canAccessRoute(activeWorkspace, path)) {
    return <Redirect to={defaultLandingPath(activeWorkspace)} />;
  }

  return <Component />;
}

export function ProtectedPage({
  component: Component,
  requireWorkspace = true,
}: {
  component: React.ComponentType;
  requireWorkspace?: boolean;
}) {
  const [location] = useLocation();

  return requireWorkspace ? (
    <WorkspaceGuard component={Component} path={location} />
  ) : (
    <Component />
  );
}
